var __defProp = Object.defineProperty;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};

// src/agent.ts
import ky from "ky";
var agent = ky.create({});

// src/client.ts
import isEqual from "lodash/isEqual";
import { useEffect, useState } from "react";
import { QueryClient, QueryClientProvider, QueryObserver } from "react-query";
import { ReactQueryDevtools } from "react-query/devtools";
var QUERY_EVENT_UPDATED = "queryUpdated";
var queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
      staleTime: 12e4
    }
  }
});
var useSubscribedQueryCache = (queryKey) => {
  const queryCache = queryClient.getQueryCache();
  const [result, setResult] = useState({
    data: void 0,
    error: void 0,
    isLoading: true
  });
  useEffect(() => {
    let unsubscribe;
    const data = queryClient.getQueryData(queryKey);
    if (data) {
      setResult({
        isLoading: false,
        data,
        error: void 0
      });
    } else {
      setResult({
        isLoading: true,
        data: void 0,
        error: void 0
      });
      unsubscribe = queryCache.subscribe((event) => {
        var _a, _b;
        const query = event == null ? void 0 : event.query;
        const data2 = (_a = query == null ? void 0 : query.state) == null ? void 0 : _a.data;
        const error = (_b = query == null ? void 0 : query.state) == null ? void 0 : _b.error;
        const type = event == null ? void 0 : event.type;
        if ((query == null ? void 0 : query.queryKey) && isEqual(query == null ? void 0 : query.queryKey, queryKey) && (data2 || error) && type === QUERY_EVENT_UPDATED) {
          setResult({
            isLoading: false,
            data: data2,
            error
          });
          unsubscribe();
        }
      });
    }
  }, []);
  return result;
};

// src/contact.ts
import { useMutation } from "react-query";
import { z } from "zod";
var CONTACT_URL = "https://cf-contact.swapglobal.com";
var ContactSchema = z.object({
  firstName: z.string().trim().min(1),
  lastName: z.string().trim().min(1),
  email: z.string().trim().email().min(1),
  company: z.string().trim().min(2),
  turnstileToken: z.string()
});
var useContactMutation = () => {
  return useMutation((data) => fetch(CONTACT_URL, {
    mode: "cors",
    method: "POST",
    body: JSON.stringify(data),
    headers: { "content-type": "application/json" }
  }));
};

// src/handlers.ts
var DEFAULT_ID_KEY = "id";
var getQueryKeys = (queryKey, variables) => {
  return typeof queryKey === "function" ? queryKey(variables) : queryKey;
};
function invalidateCachePartialMatch(prefix) {
  queryClient.invalidateQueries({
    predicate: (query) => {
      return prefix.every((prefixItem, index) => query.queryKey[index] === prefixItem);
    }
  });
}
function invalidateCache(queryKey, variables) {
  const resolvedQueryKey = getQueryKeys(queryKey, variables);
  queryClient.invalidateQueries(resolvedQueryKey);
}
function addToQueryCache(queryKey, data, variables) {
  const resolvedQueryKey = getQueryKeys(queryKey, variables);
  queryClient.setQueryData(resolvedQueryKey, () => {
    return data;
  });
}
function updateQueryCache(queryKey, data, variables) {
  const resolvedQueryKey = getQueryKeys(queryKey, variables);
  queryClient.setQueryData(resolvedQueryKey, (old) => {
    return __spreadValues(__spreadValues({}, old || {}), data);
  });
}
function updateQueryCacheCollection(queryKey, data, variables, idKey = DEFAULT_ID_KEY) {
  const resolvedQueryKey = getQueryKeys(queryKey, variables);
  queryClient.setQueryData(resolvedQueryKey, (old) => {
    return (old || [data]).map((item) => item[idKey] === data[idKey] ? data : item);
  });
}
function updateQueryDeleteFromCacheCollection(queryKey, variables, id, idKey = DEFAULT_ID_KEY) {
  const resolvedQueryKey = getQueryKeys(queryKey, variables);
  queryClient.setQueryData(resolvedQueryKey, (old) => {
    return (old || []).filter((item) => item[idKey] !== id);
  });
}
function addToQueryCacheCollection(queryKey, data, variables) {
  const resolvedQueryKey = getQueryKeys(queryKey, variables);
  queryClient.setQueryData(resolvedQueryKey, (old) => {
    return [data, ...old || []];
  });
}
function handleAddToCollectionOp(queryKey) {
  return {
    onSuccess(data, variables) {
      addToQueryCacheCollection(queryKey, data, variables);
    }
  };
}
function handleUpdateOp(queryKey) {
  return {
    onSuccess(data, variables) {
      updateQueryCache(queryKey, data, variables);
    }
  };
}
export {
  ContactSchema,
  QueryClientProvider,
  QueryObserver,
  ReactQueryDevtools,
  addToQueryCache,
  addToQueryCacheCollection,
  agent,
  handleAddToCollectionOp,
  handleUpdateOp,
  invalidateCache,
  invalidateCachePartialMatch,
  queryClient,
  updateQueryCache,
  updateQueryCacheCollection,
  updateQueryDeleteFromCacheCollection,
  useContactMutation,
  useSubscribedQueryCache
};
